import React from 'react'
import { Alert, Badge } from '@mantine/core'
import dayjs from 'dayjs'

import { AdminEvent, Event, ReporterBase } from '@/types'
import { getReporterLabel, isAdminEvent } from '@/utils/parrot'
import { formatDatetimeWithTimezone } from '@/utils/time'

// Each surface writes the holder the way it already writes a booked reporter.
type HolderFormat = { showLevel: boolean; showState: boolean }

type Hold = {
  expiresAt: string
  // Null on a reporter's own view — the holder is the viewer — and when an admin's fetch
  // could not name them.
  holder: ReporterBase | null
}

/**
 * A rescheduled event is offered back to the reporter who lost it, before anyone else can
 * see it. Only that reporter and admins are ever told.
 */
export const getFirstRefusalHold = (event: Event): Hold | null => {
  const hold = event.first_refusal_hold

  // A loaded page outlives the deadline it carries, until the next refetch clears it.
  if (!hold || !dayjs(hold.expires_at).isAfter(dayjs())) {
    return null
  }

  return {
    expiresAt: hold.expires_at,
    holder: isAdminEvent(event) ? event.first_refusal_holder : null,
  }
}

const holdTitle = (event: Event, hold: Hold): string => {
  if (!isAdminEvent(event)) {
    return 'Held for you'
  }

  return hold.holder === null
    ? 'Held for first refusal'
    : `Held for ${hold.holder.name}`
}

export const holdNotice = (event: Event, hold: Hold): string => {
  const until = formatDatetimeWithTimezone(hold.expiresAt)

  if (!isAdminEvent(event)) {
    return `This event is held for you until ${until}. After that it opens to every available reporter.`
  }

  const held = hold.holder === null ? 'held' : `held for ${hold.holder.name}`

  return `This event is ${held} until ${until}. No other reporter can see it until then. After that it opens to every available reporter.`
}

export const unassignedLabel = (
  event: AdminEvent,
  holder: HolderFormat,
): string => {
  const hold = getFirstRefusalHold(event)

  if (hold === null) {
    return 'Not assigned'
  }

  return hold.holder === null
    ? 'Not assigned — held'
    : `Not assigned — held for ${getReporterLabel(hold.holder, holder)}`
}

// The list row has no width for the holder.
export const unassignedShortLabel = (event: AdminEvent): string =>
  getFirstRefusalHold(event) === null ? 'Not assigned' : 'Not assigned — held'

export const FirstRefusalHoldBadge: React.FC<{ event: Event }> = ({
  event,
}) => {
  if (getFirstRefusalHold(event) === null) {
    return null
  }

  return (
    <Badge color="orange">
      {isAdminEvent(event) ? 'Held' : 'Held for you'}
    </Badge>
  )
}

export const FirstRefusalHoldAlert: React.FC<{
  event: Event
  hold: Hold
}> = ({ event, hold }) => (
  <Alert title={holdTitle(event, hold)} color="orange">
    {holdNotice(event, hold)}
  </Alert>
)
